/**
 * Typed access to what telegram puts on the window.
 *
 * Everything here tolerates the object being absent: the app is opened in a plain browser
 * during development, and in that case there simply is no telegram, no theme and no init
 * data. Nothing in the ui should crash because of that.
 */

export interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  section_bg_color?: string;
  section_separator_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
  accent_text_color?: string;
}

/** How telegram says a payment sheet ended. */
export type InvoiceStatus = "paid" | "cancelled" | "failed" | "pending";

/** How much of each edge of the window is covered by something the app does not own. */
export interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface TelegramWebApp {
  initData: string;
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportStableHeight: number;
  /** Whether the app was opened over the whole screen. Bot API 8.0, undefined before it. */
  isFullscreen?: boolean;
  /** What the device covers: the notch, the home indicator. Bot API 8.0. */
  safeAreaInset?: Insets;
  /** What telegram draws over the app itself: its header and its buttons. Bot API 8.0. */
  contentSafeAreaInset?: Insets;
  ready: () => void;
  expand: () => void;
  close: () => void;
  openTelegramLink: (url: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openInvoice?: (url: string, callback?: (status: InvoiceStatus) => void) => void;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

export function isInsideTelegram(): boolean {
  return Boolean(getWebApp()?.initData);
}

/**
 * The init data to authenticate with.
 *
 * Outside telegram there is none. A development build may carry one in VITE_DEV_INIT_DATA -
 * copy a real launch string into .env.local to work on the app in a normal browser. The
 * check on import.meta.env.DEV keeps that out of anything built for production, where a
 * baked in launch string would be a permanent key to somebody's account.
 */
export function getInitData(): string {
  const fromTelegram = getWebApp()?.initData;
  if (fromTelegram) {
    return fromTelegram;
  }
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_DEV_INIT_DATA ?? "";
  }
  return "";
}

/** Tell telegram the app has drawn something, and use the whole height while at it. */
export function announceReady(): void {
  const webApp = getWebApp();
  webApp?.ready();
  webApp?.expand();
}

/**
 * Open a link to a message or to a chat.
 *
 * Telegram links go through openTelegramLink, which switches to the chat instead of opening
 * a browser tab on top of the mini app.
 */
export function openInTelegram(url: string): void {
  const webApp = getWebApp();
  if (!webApp) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  if (/^https:\/\/t\.me\//.test(url)) {
    webApp.openTelegramLink(url);
    return;
  }
  webApp.openLink(url);
}

export function tapFeedback(): void {
  getWebApp()?.HapticFeedback?.selectionChanged();
}

/**
 * Open a star invoice and wait for the sheet to close.
 *
 * "paid" means telegram took the money, not that the tokens are on the balance: crediting
 * happens when telegram delivers successful_payment to the bot, which is a separate trip
 * through the pipeline. The screen polls the payment afterwards instead of believing this.
 *
 * openInvoice appeared in Bot API 6.1; an older client simply has no such method, and the
 * caller is told rather than left with a button that silently does nothing.
 */
export function openInvoice(link: string): Promise<InvoiceStatus> {
  const webApp = getWebApp();
  if (!webApp?.openInvoice) {
    return Promise.reject(new Error("openInvoice is not supported by this telegram client"));
  }
  return new Promise((resolve) => {
    webApp.openInvoice!(link, resolve);
  });
}
